import { useMemo } from 'react';
import DOMPurify from 'dompurify';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Resolve our stable relative media URLs to absolute backend URLs
const resolveMediaUrls = (html) =>
  html.replace(/(src|href)="(\/api\/help\/media\/[^"]+)"/g, (_m, attr, path) => `${attr}="${BACKEND}${path}"`);

const sanitize = (html) =>
  DOMPurify.sanitize(html, {
    ADD_TAGS: ['iframe', 'video', 'source'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'controls', 'target', 'width', 'height'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|\/api\/|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });

// Minimal fallback for un-migrated legacy content (bold + lists + line breaks)
const legacyToHtml = (text) => {
  if (!text) return '';
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text.split('\n').map((line) => {
    const t = line.trim();
    if (!t) return '';
    let l = esc(t).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    if (/^[-•]\s+/.test(t)) return `<li>${l.replace(/^[-•]\s+/, '')}</li>`;
    if (/^\d+\.\s+/.test(t)) return `<li>${l.replace(/^\d+\.\s+/, '')}</li>`;
    if (/^#{1,3}\s+/.test(t)) return `<h3>${l.replace(/^#{1,3}\s+/, '')}</h3>`;
    return `<p>${l}</p>`;
  }).join('');
};

/**
 * Renders help-article content. Rich articles (content_format === 'html') are
 * rendered as sanitised HTML supporting images, videos and YouTube/Vimeo embeds.
 * Legacy content falls back to a minimal converter.
 */
export const HelpArticleContent = ({ content, format = 'html', className = '' }) => {
  const html = useMemo(() => {
    if (!content) return '';
    const raw = format === 'html' ? content : legacyToHtml(content);
    return sanitize(resolveMediaUrls(raw));
  }, [content, format]);

  return (
    <div
      className={`help-article-content prose prose-sm max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
      data-testid="help-article-content"
    />
  );
};

export default HelpArticleContent;
